<script setup>
import { ref, reactive, computed, watch, defineProps, defineEmits } from 'vue';
import { usePayments } from '@/composables/usePayments';
import { useUser } from '@/composables/useUser';
import { useBankAccounts } from '@/composables/useBankAccounts';
import { createToaster } from '@meforma/vue-toaster';
import { PAYMENT_TYPE_DEPOSIT, PAYMENT_TYPE_WITHDRAW, PAYMENT_TYPE_DISCOUNT, PAYMENT_TYPES } from '@/constants/paymentTypes';

const props = defineProps({ isOpen: Boolean });
const emit = defineEmits(['close', 'created']);

const toaster = createToaster({ position: 'top-right', duration: 2000 });
const { createPaymentsApi } = usePayments();
const { getUsersApi } = useUser();
const { fetchActiveBankAccountsApi } = useBankAccounts();

const initialForm = {
  user_id: null,
  payment_type: PAYMENT_TYPE_DEPOSIT,
  amount: 0,
  description: '',
  bank_account_id: null,
  bank_name: '',
  bank_number: '',
};

const formData = reactive({ ...initialForm });
const submitting = ref(false);
const errorMsg = ref('');

const userSearch = ref('');
const userOptions = ref([]);
const selectedUser = ref(null);
const bankAccounts = ref([]);
let searchTimer = null;

const formattedAmount = computed(() => formData.amount ? new Intl.NumberFormat('vi-VN').format(formData.amount) : '');
const canSubmit = computed(() => {
  if (!selectedUser.value || !Number.isInteger(formData.amount) || formData.amount <= 0) return false;
  if (formData.payment_type === PAYMENT_TYPE_WITHDRAW) {
    return formData.bank_name?.trim() && formData.bank_number?.trim();
  }
  return true;
});

const fetchBanks = async () => {
  const res = await fetchActiveBankAccountsApi();
  if (res.success) {
    bankAccounts.value = res.data?.data || [];
  }
};

const onAmountInput = (e) => {
  const raw = e.target.value.replace(/\D/g, '');
  formData.amount = parseInt(raw) || 0;
};

const onUserSearch = () => {
  clearTimeout(searchTimer);
  if (!userSearch.value || userSearch.value.length < 2) {
    userOptions.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    const res = await getUsersApi({
      limit: 10,
      search: `email:${userSearch.value};name:${userSearch.value}`,
      searchFields: 'email:like;name:like',
      searchJoin: 'OR',
    });
    if (res.success) {
      userOptions.value = res.data?.data || [];
    }
  }, 300);
};

const pickUser = (u) => {
  selectedUser.value = u;
  formData.user_id = u.id;
  userSearch.value = u.email;
  userOptions.value = [];
};

const clearUser = () => {
  selectedUser.value = null;
  formData.user_id = null;
  userSearch.value = '';
  userOptions.value = [];
};

const resetForm = () => {
  Object.keys(initialForm).forEach(k => { formData[k] = initialForm[k]; });
  errorMsg.value = '';
  clearUser();
};

watch(() => props.isOpen, (val) => {
  if (val) {
    resetForm();
    fetchBanks();
  }
});

const closeForm = () => emit('close');

const handleSubmit = async () => {
  errorMsg.value = '';
  if (!selectedUser.value) { errorMsg.value = 'Vui lòng chọn người dùng'; return; }
  if (formData.amount <= 0) { errorMsg.value = 'Số tiền phải lớn hơn 0'; return; }

  submitting.value = true;
  try {
    const payload = {
      user_id: selectedUser.value.id,
      payment_type: formData.payment_type,
      amount: formData.amount,
      description: formData.description || '',
      bank_account_id: formData.payment_type === PAYMENT_TYPE_DEPOSIT ? formData.bank_account_id : null,
      bank_name: formData.payment_type === PAYMENT_TYPE_WITHDRAW ? formData.bank_name : null,
      bank_number: formData.payment_type === PAYMENT_TYPE_WITHDRAW ? formData.bank_number : null,
    };
    const res = await createPaymentsApi(payload);
    if (res.success) {
      toaster.success('Payment created. Approve it to credit the balance.');
      window.dispatchEvent(new CustomEvent('payments:changed', { detail: { reason: 'manual-created' } }));
      emit('created');
    } else {
      errorMsg.value = res.error || 'Tạo giao dịch thất bại';
    }
  } catch (err) {
    errorMsg.value = err?.message || 'Lỗi không xác định';
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <div v-if="isOpen" class="form-sidebar-overlay" @click.self="closeForm">
    <div class="form-sidebar">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h4 class="fw-bold m-0">New Payment</h4>
        <button class="btn-close" @click="closeForm"></button>
      </div>

      <form @submit.prevent="handleSubmit">
        <div class="mb-3">
          <label class="form-label fw-semibold">Người dùng <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            v-model="userSearch"
            @input="onUserSearch"
            placeholder="Nhập email hoặc tên người dùng..."
            :disabled="!!selectedUser"
          />
          <ul v-if="userOptions.length && !selectedUser" class="list-group mt-1 user-suggest">
            <li
              v-for="u in userOptions"
              :key="u.id"
              class="list-group-item list-group-item-action"
              @click="pickUser(u)"
            >
              <strong>{{ u.email }}</strong> — {{ u.name }} <small class="text-muted">(ID: {{ u.id }})</small>
            </li>
          </ul>
          <div v-if="selectedUser" class="mt-2 d-flex align-items-center gap-2">
            <span class="badge bg-info text-dark">{{ selectedUser.email }} (ID: {{ selectedUser.id }})</span>
            <button type="button" class="btn btn-sm btn-link text-danger p-0" @click="clearUser">Đổi người dùng</button>
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label fw-semibold">Loại giao dịch <span class="text-danger">*</span></label>
          <select class="form-select" v-model.number="formData.payment_type">
            <option v-for="t in [PAYMENT_TYPES.DEPOSIT, PAYMENT_TYPES.WITHDRAW, PAYMENT_TYPES.DISCOUNT]" :key="t.value" :value="t.value">
              {{ t.label }}
            </option>
          </select>
          <small class="text-muted"><strong>Deposit:</strong> cộng số dư tài khoản (hoàn tiền, cộng tay). <strong>Withdraw:</strong> trừ số dư (rút tiền). <strong>Discount:</strong> giảm giá đơn hàng (cần duyệt).</small>
        </div>

        <div v-if="formData.payment_type === PAYMENT_TYPE_DEPOSIT" class="mb-3">
          <label class="form-label fw-semibold">Ngân hàng nhận tiền (Tùy chọn)</label>
          <select class="form-select" v-model="formData.bank_account_id">
            <option :value="null">-- Mặc định hệ thống --</option>
            <option v-for="bank in bankAccounts" :key="bank.id" :value="bank.id">
              {{ bank.short_name }} - {{ bank.account_number }} ({{ bank.account_name }})
            </option>
          </select>
          <small class="text-muted">Nếu chọn, QR code sẽ được tạo riêng cho tài khoản này.</small>
        </div>

        <div v-if="formData.payment_type === PAYMENT_TYPE_WITHDRAW" class="mb-3">
          <label class="form-label fw-semibold">Tên ngân hàng <span class="text-danger">*</span></label>
          <input type="text" class="form-control" v-model="formData.bank_name" placeholder="Ví dụ: Vietcombank, MB Bank" />
        </div>

        <div v-if="formData.payment_type === PAYMENT_TYPE_WITHDRAW" class="mb-3">
          <label class="form-label fw-semibold">Số tài khoản <span class="text-danger">*</span></label>
          <input type="text" class="form-control" v-model="formData.bank_number" placeholder="Ví dụ: 1234567890" />
        </div>

        <div class="mb-3">
          <label class="form-label fw-semibold">Số tiền (VND) <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            :value="formattedAmount"
            @input="onAmountInput"
            placeholder="Ví dụ: 100.000"
          />
        </div>

        <div class="mb-3">
          <label class="form-label fw-semibold">Ghi chú</label>
          <textarea
            class="form-control"
            v-model="formData.description"
            rows="3"
            placeholder="Lý do tạo giao dịch thủ công..."
          ></textarea>
        </div>

        <div v-if="errorMsg" class="alert alert-danger py-2 small">{{ errorMsg }}</div>

        <div class="d-flex gap-2 mt-4">
          <button type="button" class="btn btn-secondary w-100" @click="closeForm">Cancel</button>
          <button type="submit" class="btn btn-primary w-100" :disabled="submitting || !canSubmit">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
            Create
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.form-sidebar-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0,0,0,0.4);
  z-index: 9999 !important;
}
.form-sidebar {
  position: fixed;
  top: 0; right: 0;
  width: 460px; max-width: 100%; height: 100%;
  background: white;
  padding: 30px;
  box-shadow: -5px 0 15px rgba(0,0,0,0.1);
  animation: slideIn 0.3s ease-out;
  overflow-y: auto;
}
@keyframes slideIn {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
.user-suggest {
  max-height: 220px;
  overflow-y: auto;
}
.user-suggest .list-group-item {
  cursor: pointer;
}
</style>
